import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { StepIndicator } from '../components/StepIndicator';
import { OnboardingData, initialOnboardingData } from '../types/onboarding';
import { UserDetails } from '../components/steps/UserDetails';
import { StatesLicensed } from '../components/steps/StatesLicensed';
import { AccessConfig } from '../components/steps/AccessConfig';
import { LoanTypeCapacities } from '../components/steps/LoanTypeCapacities';
import { PackageLicenses } from '../components/steps/PackageLicenses';
import { ReviewCreate } from '../components/steps/ReviewCreate';
import { OffboardingPanel } from '../components/OffboardingPanel';
const STEPS = [{
  id: 1,
  title: 'User Details',
  description: 'Enter the core information for the new Salesforce user and verify license availability.'
}, {
  id: 2,
  title: 'States Licensed',
  description: 'Select the states where the user is licensed to operate.'
}, {
  id: 3,
  title: 'Access & Configuration',
  description: 'Configure operational settings, group memberships, and permission sets.'
}, {
  id: 4,
  title: 'Loan Type Capacities',
  description: 'Configure daily lead assignment limits per loan type.'
}, {
  id: 5,
  title: 'Package Licenses',
  description: 'Assign necessary package licenses to the user.'
}, {
  id: 6,
  title: 'Review & Create',
  description: 'Review all configurations before creating the user and associated records.'
}];
const OFFBOARDING_STEPS = [{
  id: 1,
  title: 'Select Users',
  description: 'Search and select users to deactivate.'
}, {
  id: 2,
  title: 'Review & Confirm',
  description: 'Review selections and confirm deactivation.'
}, {
  id: 3,
  title: 'Complete',
  description: 'Deactivation processed successfully.'
}];
interface OnboardingWizardProps {
  mode: 'onboarding' | 'offboarding';
  setMode: (mode: 'onboarding' | 'offboarding') => void;
}
export function OnboardingWizard({
  mode,
  setMode
}: OnboardingWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [offboardingStep, setOffboardingStep] = useState(0);
  const [formData, setFormData] = useState<OnboardingData>(initialOnboardingData);
  const [canAdvance, setCanAdvance] = useState(false);
  const updateFormData = (updates: Partial<OnboardingData>) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };
  const handleNext = () => {
    if (currentStep < STEPS.length - 1 && canAdvance) {
      setCurrentStep(prev => prev + 1);
    }
  };
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  const handleStepClick = (index: number) => {
    if (index < currentStep) {
      setCurrentStep(index);
    }
  };
  const isLastStep = currentStep === STEPS.length - 1;
  const renderStepContent = () => {
    const props = {
      data: formData,
      updateData: updateFormData,
      setCanAdvance
    };
    switch (currentStep) {
      case 0:
        return <UserDetails {...props} data-id="element-323" />;
      case 1:
        return <StatesLicensed {...props} data-id="element-324" />;
      case 2:
        return <AccessConfig {...props} data-id="element-325" />;
      case 3:
        return <LoanTypeCapacities {...props} data-id="element-326" />;
      case 4:
        return <PackageLicenses {...props} data-id="element-327" />;
      case 5:
        return <ReviewCreate {...props} onBack={handleBack} data-id="element-328" />;
      default:
        return null;
    }
  };
  return <div className="min-h-screen w-full bg-[#F0F2F5] font-sans text-gray-900 pb-12" data-id="element-329">
      {/* Top Header Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6" data-id="element-330">
        <div className="flex items-center" data-id="element-331">
          {/* Logo */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="flex-shrink-0" data-id="element-332">
            <img src="https://cdn.magicpatterns.com/uploads/m7g4CL4BVvafgftBPsW3VX/image.png" alt="Truss Financial Group" className="h-10" data-id="element-333" />
          </motion.div>
        </div>
      </div>

      {/* Main Content Card */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-334">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col md:flex-row min-h-[600px] overflow-hidden" data-id="element-335">
          {/* Left Column: Navigation */}
          <div className="w-full md:w-[240px] border-b md:border-b-0 md:border-r border-gray-100 flex-shrink-0 bg-white" data-id="element-336">
            <StepIndicator steps={mode === 'offboarding' ? OFFBOARDING_STEPS : STEPS} currentStep={mode === 'offboarding' ? offboardingStep : currentStep} onStepClick={handleStepClick} mode={mode} setMode={setMode} data-id="element-337" />
          </div>

          {/* Right Column: Form Area */}
          <div className="flex-1 flex flex-col bg-white relative" data-id="element-338">
            {mode === 'offboarding' ? <OffboardingPanel onStepChange={setOffboardingStep} data-id="element-339" /> : <div className="flex-1 p-6 md:p-10 overflow-y-auto" data-id="element-340">
                {/* Step Header */}
                <div className="mb-8" data-id="element-341">
                  <h1 className="text-xl font-bold text-gray-900 mb-2" data-id="element-342">
                    {STEPS[currentStep].title}
                  </h1>
                  <p className="text-sm text-gray-500" data-id="element-343">
                    {STEPS[currentStep].description}
                  </p>
                </div>

                {/* Step Content */}
                <AnimatePresence mode="wait" data-id="element-344">
                  <motion.div key={currentStep} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2,
                ease: 'easeInOut'
              }} data-id="element-345">
                    {renderStepContent()}
                  </motion.div>
                </AnimatePresence>

                {/* Inline Navigation (Hidden on last step as it handles its own submit) */}
                {!isLastStep && <div className="mt-12 pt-6 border-t border-gray-100 flex items-center justify-between" data-id="element-346">
                    {currentStep > 0 ? <button onClick={handleBack} className="text-sm font-medium text-gray-500 hover:text-gray-900 hover:underline transition-colors" data-id="element-347">
                          Previous
                        </button> : <div data-id="element-348"></div> // Spacer
              }

                    <button onClick={handleNext} disabled={!canAdvance} className={`px-8 py-2.5 rounded-full font-medium text-sm transition-all shadow-sm ${!canAdvance ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#2563EB] text-white hover:bg-[#1D4ED8]'}`} data-id="element-349">
                      Next
                    </button>
                  </div>}
              </div>}
          </div>
        </div>
      </motion.div>
    </div>;
}